import {
  Checkbox,
  InputIconWrapper,
  NumberInput
} from '@library/components/inputs';
import {
  getAssignedRolesByPermission,
  getPermissionsByCurrentRoles
} from '@bo-utils/user';
import { useCallback, useMemo, useState } from 'react';
import { Button } from '@library/components/buttons';
import type { FormattedPermission } from '@library/types/permissions';
import { NUMBER_INPUT_DEFAULT_CLASSES } from '@library/components/inputs/NumberInput';
import type { UserRoleAttributes } from '@library/types/user';
import { useDebounceFn } from '@library/hooks/useDebounce';
import { useRoles } from '@bo-hooks/roles/useRoles';
import whiteArrowLeftIcon from '@library/assets/images/icons/white-arrow-left.svg';

export interface EditUserPermissionsProps {
  onClose: () => void;
  permissions: Array<FormattedPermission> | undefined;
  userRoles: Array<UserRoleAttributes>;
}

export const EditUserPermissions = ({
  onClose,
  permissions,
  userRoles
}: EditUserPermissionsProps) => {
  const { currentRoles, rolesWithPermissions } = useRoles({ roles: userRoles });
  const [tooltipValues, setTooltipValues] = useState<Array<string>>([]);

  const formattedPermissions = useMemo(() => {
    if (!permissions) return [];

    return getPermissionsByCurrentRoles(
      currentRoles,
      permissions,
      rolesWithPermissions
    );
  }, [currentRoles, permissions, rolesWithPermissions]);

  const { debounce: handleMouseEnter, cancel: cancelMouseEnter } =
    useDebounceFn((permissionId) => {
      if (currentRoles && currentRoles?.length <= 1) {
        return;
      }

      const rolesByPermission = getAssignedRolesByPermission(
        currentRoles,
        rolesWithPermissions,
        permissionId as string
      );

      setTooltipValues(rolesByPermission);
    }, 500);

  const handleMouseLeave = useCallback(() => {
    cancelMouseEnter();
    setTooltipValues([]);
  }, [cancelMouseEnter]);

  const permissionsContent = useMemo(
    () =>
      formattedPermissions?.map((permission) => (
        <div
          key={permission.name}
          onMouseEnter={() => handleMouseEnter(permission.id)}
          onMouseLeave={handleMouseLeave}
        >
          <Checkbox
            wrapperClassName="hover:bg-soFresh-120 flex flex-row-reverse justify-between"
            id={permission.id}
            checked={permission.hasPermission}
            onCheckboxChange={() => null}
            label={permission.label}
            labelClassName="m-0"
            disabled
          />

          {permission.has_transaction_limit && (
            <div className="text-soClean-100 mt-1 flex items-center justify-between text-sm">
              <p>Transaction Max</p>
              <InputIconWrapper
                icon="$"
                className="flex flex-col gap-y-2"
                iconClassName="top-[5px]"
              >
                <NumberInput
                  className={`${NUMBER_INPUT_DEFAULT_CLASSES} !w-28 pl-10 !leading-5`}
                  value={permission.transactionMaxValue}
                  // onValueChange={(value) => null} Commented out due to ts error
                  disabled
                />
              </InputIconWrapper>
            </div>
          )}
        </div>
      )),
    [formattedPermissions, handleMouseEnter, handleMouseLeave]
  );

  const permissionsEnabledByRoles = useMemo(
    () =>
      tooltipValues.length > 0 && (
        <div className="bg-soFresh-120 border-soFresh-140 absolute left-0 top-[64px] z-[54] w-full border-b border-solid px-4 pb-3 pt-2">
          <p className="text-soClean-100 text-xs uppercase leading-6">
            Permission enabled by role(s):
          </p>

          <div className="flex flex-wrap gap-2 pt-1">
            {tooltipValues.map((role) => (
              <span
                key={role}
                className="text-soClean-100 bg-soFresh-150 rounded-2xl px-3 py-2 text-xs"
              >
                {role}
              </span>
            ))}
          </div>
        </div>
      ),
    [tooltipValues]
  );

  return (
    <>
      <Button
        className="text-soClean-100 m-4 mb-3 flex items-center"
        onClick={onClose}
      >
        <img
          alt="Left Arrow icon"
          className="size-3.5"
          src={whiteArrowLeftIcon}
        />
        <span className="ml-1.5 text-sm">Back</span>
      </Button>
      <div
        className="flex flex-col gap-y-2 p-4"
        data-testid="EditUserPermissions"
      >
        <div className="mb-1 flex items-center justify-between">
          <p className="text-soClean-100 text-xs uppercase leading-6">
            All Permissions
          </p>
        </div>
        {permissionsEnabledByRoles}
        {permissionsContent}
      </div>
    </>
  );
};
